import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Loader2, Pencil, Trash2, Mail, Phone } from "lucide-react";

const inputCls = "w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#1A365D] outline-none";
const empty = { name: "", email: "", phone: "", notes: "" };

export default function Guests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/guests").then(({ data }) => setItems(data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ ...g }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await api.put(`/guests/${editing.id}`, form);
      else await api.post("/guests", form);
      toast.success(editing ? "Guest updated" : "Guest added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete guest ${g.name}?`)) return;
    await api.delete(`/guests/${g.id}`); toast.success("Guest deleted"); load();
  };

  return (
    <div data-testid="guests-page" className="max-w-5xl">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Directory</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-medium">Guests</h1>
        </div>
        <button data-testid="add-guest-button" onClick={openNew} className="bg-[#1A365D] text-white hover:bg-[#2A4A7F] rounded-full px-5 py-2.5 font-medium transition-colors flex items-center gap-2">
          <Plus size={18} /> Add Guest
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center pt-20"><Loader2 className="animate-spin text-[#1A365D]" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-gray-400">No guests added yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((g) => (
            <div key={g.id} data-testid={`guest-card-${g.id}`} className="bg-white border border-black/5 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-[#1A365D] text-white flex items-center justify-center font-semibold">{g.name.charAt(0).toUpperCase()}</div>
                <h3 className="font-display text-xl font-medium">{g.name}</h3>
              </div>
              {g.email && <p className="text-sm text-gray-600 flex items-center gap-2 mb-1"><Mail size={14} /> {g.email}</p>}
              {g.phone && <p className="text-sm text-gray-600 flex items-center gap-2"><Phone size={14} /> {g.phone}</p>}
              {g.notes && <p className="text-sm text-gray-400 mt-3 italic">{g.notes}</p>}
              <div className="flex gap-2 mt-5">
                <button data-testid={`edit-guest-${g.id}`} onClick={() => openEdit(g)} className="flex-1 bg-[#F4F1EA] text-[#1A365D] hover:bg-[#E3D5CA] rounded-full px-4 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"><Pencil size={15} /> Edit</button>
                <button data-testid={`delete-guest-${g.id}`} onClick={() => remove(g)} className="px-3 py-2 rounded-full text-gray-400 hover:text-white hover:bg-[#EF4444] transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A365D]/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div data-testid="guest-dialog" className="bg-white rounded-2xl w-full max-w-lg p-8 shadow-xl animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl font-medium">{editing ? "Edit Guest" : "New Guest"}</h2>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700"><X size={22} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Name</label><input data-testid="guest-name-input" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Email</label><input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Phone</label><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Notes</label><textarea rows="3" className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="bg-[#F4F1EA] text-[#1A365D] hover:bg-[#E3D5CA] rounded-full px-6 py-2.5 font-medium transition-colors">Cancel</button>
                <button data-testid="save-guest-button" type="submit" disabled={saving} className="bg-[#1A365D] text-white hover:bg-[#2A4A7F] rounded-full px-6 py-2.5 font-medium transition-colors flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />} Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
